import type {
  SalesOrder,
  PackedItem,
  SalesOrderItemCustomization,
} from "@/lib/erp/types/order";

/** Map of component_item_code → selected_variant_code */
export type BundleVariantSelections = Record<string, string>;

export interface OrderItemBundleData {
  packedItems: PackedItem[];
  customizations: SalesOrderItemCustomization[];
  isBundle: boolean;
  /** Parsed bundle_variant_selections from the parent SO Item */
  variantSelections: BundleVariantSelections;
}

/**
 * Parse a customization field label that may have a child item code prefix.
 * Format: "CS-SNP-WS:ต้องการปักชื่อ/โลโก้ หรือไม่" → { childItemCode: "CS-SNP-WS", cleanLabel: "ต้องการปักชื่อ/โลโก้ หรือไม่" }
 * Format: "ต้องการปัก" → { childItemCode: null, cleanLabel: "ต้องการปัก" }
 */
export function parseCustomizationLabel(fieldLabel: string): {
  childItemCode: string | null;
  cleanLabel: string;
} {
  const colonIdx = fieldLabel.indexOf(":");
  if (colonIdx === -1) {
    return { childItemCode: null, cleanLabel: fieldLabel };
  }

  const left = fieldLabel.slice(0, colonIdx);
  const right = fieldLabel.slice(colonIdx + 1).trim();

  // Item codes are uppercase alphanumeric with hyphens, min 2 chars
  const ITEM_CODE_PATTERN = /^[A-Z0-9][-A-Z0-9]*$/;
  if (left.length >= 2 && ITEM_CODE_PATTERN.test(left)) {
    return { childItemCode: left, cleanLabel: right };
  }

  // Left part doesn't look like an item code — treat whole string as label
  return { childItemCode: null, cleanLabel: fieldLabel };
}

/**
 * Correlate packed_items and item_customizations with their parent Sales Order Item rows.
 * Returns a Map keyed by item row `name` (Frappe row ID).
 */
export function correlateOrderBundleData(
  doc: SalesOrder
): Map<string, OrderItemBundleData> {
  const result = new Map<string, OrderItemBundleData>();

  // Initialize entries for each item that has a name (row ID)
  for (const item of doc.items ?? []) {
    if (!item.name) continue;
    let variantSelections: BundleVariantSelections = {};
    if (item.bundle_variant_selections) {
      try {
        variantSelections = JSON.parse(item.bundle_variant_selections);
      } catch {
        // malformed JSON — ignore
      }
    }
    result.set(item.name, {
      packedItems: [],
      customizations: [],
      isBundle: false,
      variantSelections,
    });
  }

  // Group packed_items by parent SO Item row
  for (const packed of doc.packed_items ?? []) {
    const entry = result.get(packed.parent_detail_docname);
    if (entry) {
      entry.packedItems.push(packed);
      entry.isBundle = true;
    }
  }

  // Group item_customizations by parent SO Item row, filtering empty entries
  for (const cust of doc.item_customizations ?? []) {
    // Skip customizations with no meaningful value
    const hasValue = cust.value && cust.value.trim() !== "";
    const hasFile = cust.file_url && cust.file_url.trim() !== "";
    if (!hasValue && !hasFile) continue;

    const entry = result.get(cust.parent_item_row);
    if (entry) {
      entry.customizations.push(cust);
    }
  }

  return result;
}
